/**
 * CR311: Section Checklist Panel
 * CR314: Updated to use V2 block metadata + support header/starburst/ribbon toggles
 * CR316: Added Front/Back surface switcher for both surfaces
 * CR309: Added drag-and-drop block reordering per surface
 * CR319: Added Promo Badges and Details Content accordions below block toggles
 * 
 * Transforms Structure panel into a checklist with:
 * - Surface selector (Front / Back)
 * - Checkbox + Section name + Status pill
 * - Drag handles for reordering blocks
 * - Collapsible groups (Core Sections / Value Boxes / Actions / Admin)
 * - Inline warning icons for missing fields
 * - One-click actions (Disable section / Use fallback)
 * - V2 block support for both surfaces
 * - Promo Badges accordion (CR319)
 * - Details Content accordion (CR319)
 */

import { useMemo, useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  ChevronDown,
  ChevronRight,
  Check,
  AlertTriangle,
  Info,
  EyeOff,
  Sparkles,
  Layers,
  LayoutList,
  GripVertical,
  Lock,
  RotateCcw,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ExtendedSectionToggles } from "@/components/admin/command-center/SectionTogglesPanel";
import type { FieldDebug } from "@/lib/deals/fieldResolver";
import {
  PRIMARY_BLOCK_METADATA,
  DETAILS_BLOCK_METADATA,
  getBlockDisplayLabel,
  getUIGroupDisplayName,
  groupBlocksByUIGroup,
  type BlockUIGroup,
  type BlockMetadata,
  type BlockSurface,
} from "@/lib/deal-cards/specV2";

// CR319: Import accordion components
import { PromoBadgesAccordion, type PromoBadgesConfig, DEFAULT_PROMO_BADGES_CONFIG } from "./PromoBadgesAccordion";
import { DetailsContentAccordion, type DetailsContentConfig, DEFAULT_DETAILS_CONTENT_CONFIG } from "./DetailsContentAccordion";

// CR314: V2 block toggles type for header/starburst/ribbon
export interface V2BlockToggles {
  [blockId: string]: boolean;
}

// CR316: Per-surface block toggles
export interface SurfaceBlockToggles {
  primary?: V2BlockToggles;
  details?: V2BlockToggles;
}

// CR309: Per-surface block order
export interface SurfaceBlockOrder {
  primary?: string[];
  details?: string[];
}

interface SectionChecklistProps {
  sections: ExtendedSectionToggles;
  onSectionToggle: (key: keyof ExtendedSectionToggles, value: boolean) => void;
  /** CR314: V2 block toggles for header/starburst/ribbon/meta_pills */
  v2BlockToggles?: V2BlockToggles;
  /** CR314: Handler for V2 block toggle changes */
  onV2BlockToggle?: (blockId: string, enabled: boolean) => void;
  /** CR316: Per-surface block toggles (overrides v2BlockToggles for surface-specific) */
  surfaceBlockToggles?: SurfaceBlockToggles;
  /** CR316: Handler for surface-specific block toggle changes */
  onSurfaceBlockToggle?: (surface: BlockSurface, blockId: string, enabled: boolean) => void;
  /** CR309: Per-surface block order */
  surfaceBlockOrder?: SurfaceBlockOrder;
  /** CR309: Handler for block order changes */
  onSurfaceBlockOrderChange?: (surface: BlockSurface, newOrder: string[]) => void;
  /** CR314: Style overrides for legacy show* flags */
  styleOverrides?: Record<string, unknown>;
  /** CR314: Handler for style override changes */
  onStyleOverrideChange?: (key: string, value: boolean) => void;
  /** Field map for determining missing fields */
  fieldMap?: FieldDebug[];
  /** Warnings grouped by section */
  warnings?: { section: string; missingField: string; message: string }[];
  /** CR314: Deal type for label resolution */
  dealType?: string;
  /** CR309: Callback when drag state changes (to pause preview) */
  onDragStateChange?: (isDragging: boolean) => void;
  /** CR319: Promo badges config */
  promoBadgesConfig?: PromoBadgesConfig;
  /** CR319: Handler for promo badges config changes */
  onPromoBadgesChange?: (config: PromoBadgesConfig) => void;
  /** CR319: Details content config */
  detailsContentConfig?: DetailsContentConfig;
  /** CR319: Handler for details content config changes */
  onDetailsContentChange?: (config: DetailsContentConfig) => void;
  className?: string;
}

// CR314: Map V2 block IDs to legacy section keys for dual-write
const V2_BLOCK_TO_LEGACY_SECTION: Record<string, keyof ExtendedSectionToggles | undefined> = {
  headline: "headline",
  promo_badges: "promo_badges",
  details_pills: "details_pills",
  dates: "dates",
  value_box: "effective_value_box",
  spend_box: "qualifying_spend_box",
  reward_box: "reward_box",
  eligibility_box: "eligibility_box",
  cta_button: "claim_button",
  merchant_category_chips: "merchant_category_chips",
  confidence_indicator: "confidence_indicator",
  debug_strip: "debug_strip",
  ribbon: "ribbon",
  // V2-only blocks (no legacy equivalent)
  header: undefined,
  starburst: undefined,
  meta_pills: undefined,
  details_cta: undefined, // CR309: Details CTA block (v2-only)
  // Details surface blocks
  back_header: undefined,
  back_nav: undefined,
  terms: undefined,
  fine_print: undefined,
  source: undefined,
  evidence: undefined,
  validator: undefined,
  details_actions: undefined,
};

// CR314: Required fields for validation (keyed by block ID)
const REQUIRED_FIELDS_BY_BLOCK: Record<string, string[] | undefined> = {
  headline: ["headline_mechanic"],
  dates: ["valid_from", "valid_until"],
  value_box: ["computed_value_cad", "effective_percent_back", "discount_percent"],
  spend_box: ["qualifying_spend_cad"],
  reward_box: ["bonus_value_cad", "points_amount"],
  cta_button: ["claim_url", "deal_url"],
};

type SectionStatus = "ok" | "missing" | "fallback";

// CR316: Get effective toggle state for a block with surface awareness
function getBlockToggleState(
  blockId: string,
  surface: BlockSurface,
  sections: ExtendedSectionToggles,
  v2BlockToggles?: V2BlockToggles,
  surfaceBlockToggles?: SurfaceBlockToggles,
  styleOverrides?: Record<string, unknown>
): boolean {
  // 1. Check surface-specific blockToggles first (highest precedence for CR316)
  const surfaceToggles = surfaceBlockToggles?.[surface];
  if (surfaceToggles && typeof surfaceToggles[blockId] === 'boolean') {
    return surfaceToggles[blockId];
  }
  
  // 2. Check V2 blockToggles (generic)
  if (v2BlockToggles && typeof v2BlockToggles[blockId] === 'boolean') {
    return v2BlockToggles[blockId];
  }
  
  // 3. Check styleOverrides for legacy show* flags (primary surface only)
  // CR309: REMOVED ribbon from legacy fallback - ribbon is now SSOT via surfaceBlockToggles only
  if (surface === 'primary' && styleOverrides) {
    if (blockId === 'starburst' && typeof styleOverrides.showStarburst === 'boolean') {
      return styleOverrides.showStarburst;
    }
    // CR309: ribbon check REMOVED - blockToggles.ribbon is the ONLY source
    if (blockId === 'header') {
      const showLogo = styleOverrides.showMerchantLogo;
      const showName = styleOverrides.showMerchantName;
      // Header is on unless both are explicitly false
      if (showLogo === false && showName === false) {
        return false;
      }
      return true;
    }
  }
  
  // 4. Check legacy sections (primary surface only)
  if (surface === 'primary') {
    const legacyKey = V2_BLOCK_TO_LEGACY_SECTION[blockId];
    if (legacyKey && sections[legacyKey] !== undefined) {
      return sections[legacyKey];
    }
  }
  
  // 5. Default to true
  return true;
}

export function SectionChecklist({
  sections,
  onSectionToggle,
  v2BlockToggles,
  onV2BlockToggle,
  surfaceBlockToggles,
  onSurfaceBlockToggle,
  surfaceBlockOrder,
  onSurfaceBlockOrderChange,
  styleOverrides,
  onStyleOverrideChange,
  fieldMap = [],
  warnings = [],
  dealType = "UNCLASSIFIED",
  onDragStateChange,
  // CR319: Promo badges and details content props
  promoBadgesConfig = DEFAULT_PROMO_BADGES_CONFIG,
  onPromoBadgesChange,
  detailsContentConfig = DEFAULT_DETAILS_CONTENT_CONFIG,
  onDetailsContentChange,
  className,
}: SectionChecklistProps) {
  // CR316: Track active surface
  const [activeSurface, setActiveSurface] = useState<BlockSurface>("primary");
  
  // CR309: Track active drag item for overlay
  const [activeId, setActiveId] = useState<string | null>(null);
  
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    initial.add("core"); // Core sections expanded by default
    return initial;
  });
  
  // CR309: Setup dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Prevent accidental drags
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // CR316: Get grouped blocks for the active surface
  const groupedBlocks = useMemo(() => {
    return groupBlocksByUIGroup(activeSurface);
  }, [activeSurface]);
  
  // Get all blocks for the active surface
  const surfaceBlocks = useMemo(() => {
    return activeSurface === "primary" ? PRIMARY_BLOCK_METADATA : DETAILS_BLOCK_METADATA;
  }, [activeSurface]);
  
  // CR309: Get current block order for active surface (or default order)
  const currentBlockOrder = useMemo(() => {
    const order = surfaceBlockOrder?.[activeSurface];
    if (order && order.length > 0) {
      return order;
    }
    // Default order from metadata
    return surfaceBlocks.map(b => b.id);
  }, [surfaceBlockOrder, activeSurface, surfaceBlocks]);
  
  // CR309: Check if order differs from default
  const hasCustomOrder = useMemo(() => {
    const defaultOrder = surfaceBlocks.map(b => b.id);
    const order = surfaceBlockOrder?.[activeSurface] || [];
    if (order.length === 0) return false;
    if (order.length !== defaultOrder.length) return true;
    return order.some((id, idx) => id !== defaultOrder[idx]);
  }, [surfaceBlocks, surfaceBlockOrder, activeSurface]);
  
  // Get status for a block
  const getBlockStatus = useCallback((
    blockId: string,
    enabled: boolean
  ): SectionStatus => {
    if (!enabled) return "ok"; // Disabled blocks are OK
    
    // Check if this block has a warning
    const legacyKey = V2_BLOCK_TO_LEGACY_SECTION[blockId];
    const hasWarning = warnings.some(w => w.section === blockId || w.section === legacyKey);
    if (hasWarning) return "missing";
    
    // Check field map for required fields
    const requiredFields = REQUIRED_FIELDS_BY_BLOCK[blockId];
    if (requiredFields && fieldMap.length > 0) {
      const hasAnyRequired = requiredFields.some(fieldKey => {
        const field = fieldMap.find(f => f.key === fieldKey);
        return field?.status === "present";
      });
      if (!hasAnyRequired) return "missing";
    }
    
    return "ok";
  }, [warnings, fieldMap]);
  
  // Get warning details for a block
  const getWarningDetails = useCallback((blockId: string) => {
    const legacyKey = V2_BLOCK_TO_LEGACY_SECTION[blockId];
    return warnings.filter(w => w.section === blockId || w.section === legacyKey);
  }, [warnings]);
  
  // Toggle group expansion
  const toggleGroup = useCallback((group: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }, []);
  
  // CR316: Handle block toggle change - route to correct handler based on surface
  const handleBlockToggle = useCallback((blockId: string, enabled: boolean) => {
    // CR316: For details surface, always use surface-specific handler
    if (activeSurface === 'details' && onSurfaceBlockToggle) {
      onSurfaceBlockToggle('details', blockId, enabled);
      return;
    }
    
    // CR305: Starburst MUST use surfaceBlockToggles — NEVER styleOverrides
    // This matches ribbon (CR310) and ensures SSOT for all block toggles
    if (blockId === 'starburst' && onSurfaceBlockToggle) {
      onSurfaceBlockToggle('primary', blockId, enabled);
      return;
    }
    // CR310: Ribbon MUST use surfaceBlockToggles - NEVER styleOverrides
    // This ensures SSOT for ribbon visibility as per CR309/CR310
    if (blockId === 'ribbon' && onSurfaceBlockToggle) {
      onSurfaceBlockToggle('primary', blockId, enabled);
      return;
    }
    
    // CR316: Use surface-specific handler if available for primary
    if (onSurfaceBlockToggle) {
      onSurfaceBlockToggle('primary', blockId, enabled);
    }
    
    // For V2-only blocks, use V2 handler
    if (onV2BlockToggle && (blockId === 'header' || blockId === 'meta_pills' || !V2_BLOCK_TO_LEGACY_SECTION[blockId])) {
      onV2BlockToggle(blockId, enabled);
      return;
    }
    
    // For blocks with legacy mappings, use legacy handler + dual-write to V2
    const legacyKey = V2_BLOCK_TO_LEGACY_SECTION[blockId];
    if (legacyKey) {
      onSectionToggle(legacyKey, enabled);
    }
    
    // Also write to V2 blockToggles if handler provided
    if (onV2BlockToggle) {
      onV2BlockToggle(blockId, enabled);
    }
  }, [activeSurface, onSectionToggle, onV2BlockToggle, onSurfaceBlockToggle, onStyleOverrideChange]);
  
  // CR309: Setup dnd-kit sensors
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    onDragStateChange?.(true);
  }, [onDragStateChange]);
  
  // CR309/CR451: Get fixed block IDs for current surface
  const fixedBlockIds = useMemo(() => {
    return activeSurface === "details" 
      ? new Set(["back_header", "back_nav"])
      : new Set<string>();
  }, [activeSurface]);
  
  // CR309/CR312: Handle drag end - reorder blocks within orderable subset
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    onDragStateChange?.(false);
    
    const { active, over } = event;
    if (!over || active.id === over.id || !onSurfaceBlockOrderChange) return;
    
    // Get the orderable block IDs (excluding fixed blocks)
    const orderableIds = currentBlockOrder.filter(id => !fixedBlockIds.has(id));
    
    // Find indices within the orderable subset
    const oldIndex = orderableIds.indexOf(active.id as string);
    const newIndex = orderableIds.indexOf(over.id as string);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Reorder only the orderable items
    const newOrderableIds = arrayMove(orderableIds, oldIndex, newIndex);
    
    // Reconstruct full order: fixed blocks stay in their original positions
    // CR451: For details surface: only back_header, back_nav fixed at top; details_actions is reorderable
    // For primary surface: currently no fixed blocks
    const fixedTop = currentBlockOrder.filter(id => 
      fixedBlockIds.has(id) && (id === "back_header" || id === "back_nav")
    );
    
    const fullOrder = [...fixedTop, ...newOrderableIds];
    
    console.log("[SectionChecklist] handleDragEnd:", { 
      activeId: active.id, 
      overId: over.id, 
      oldIndex, 
      newIndex, 
      newOrder: fullOrder 
    });
    
    onSurfaceBlockOrderChange(activeSurface, fullOrder);
  }, [currentBlockOrder, activeSurface, fixedBlockIds, onSurfaceBlockOrderChange, onDragStateChange]);
  
  // CR309/CR312: Reset block order to default (clear custom order)
  const handleResetOrder = useCallback(() => {
    if (!onSurfaceBlockOrderChange) return;
    // Pass empty array to clear custom order - renderer will use default metadata order
    onSurfaceBlockOrderChange(activeSurface, []);
  }, [activeSurface, onSurfaceBlockOrderChange]);
  
  // CR309: Get active block for drag overlay
  const activeBlock = useMemo(() => {
    if (!activeId) return null;
    return surfaceBlocks.find(b => b.id === activeId);
  }, [activeId, surfaceBlocks]);
  
  // Count enabled blocks for current surface
  const enabledCount = useMemo(() => {
    return surfaceBlocks.filter(block => {
      return getBlockToggleState(block.id, activeSurface, sections, v2BlockToggles, surfaceBlockToggles, styleOverrides);
    }).length;
  }, [surfaceBlocks, activeSurface, sections, v2BlockToggles, surfaceBlockToggles, styleOverrides]);
  
  // Count warnings for current surface
  const warningCount = useMemo(() => {
    return surfaceBlocks.filter(block => {
      const enabled = getBlockToggleState(block.id, activeSurface, sections, v2BlockToggles, surfaceBlockToggles, styleOverrides);
      if (!enabled) return false;
      return getBlockStatus(block.id, enabled) === "missing";
    }).length;
  }, [surfaceBlocks, activeSurface, sections, v2BlockToggles, surfaceBlockToggles, styleOverrides, getBlockStatus]);
  
  // Render a group of blocks
  const renderGroup = (group: BlockUIGroup) => {
    const blocks = groupedBlocks[group];
    if (!blocks || blocks.length === 0) return null;
    
    const isExpanded = expandedGroups.has(group);
    const groupEnabledCount = blocks.filter(b => 
      getBlockToggleState(b.id, activeSurface, sections, v2BlockToggles, surfaceBlockToggles, styleOverrides)
    ).length;
    const groupWarningCount = blocks.filter(b => {
      const enabled = getBlockToggleState(b.id, activeSurface, sections, v2BlockToggles, surfaceBlockToggles, styleOverrides);
      return enabled && getBlockStatus(b.id, enabled) === "missing";
    }).length;
    
    return (
      <Collapsible key={group} open={isExpanded} onOpenChange={() => toggleGroup(group)}>
        <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <span className="text-xs font-medium">{getUIGroupDisplayName(group)}</span>
            <span className="text-[10px] text-muted-foreground">
              ({groupEnabledCount}/{blocks.length})
            </span>
          </div>
          {groupWarningCount > 0 && (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          )}
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="ml-5 space-y-1 pt-1">
            {blocks.map((block) => {
              const enabled = getBlockToggleState(block.id, activeSurface, sections, v2BlockToggles, surfaceBlockToggles, styleOverrides);
              const status = getBlockStatus(block.id, enabled);
              const blockWarnings = getWarningDetails(block.id);
              const displayLabel = getBlockDisplayLabel(block.id, dealType);
              
              return (
                <div
                  key={block.id}
                  className={cn(
                    "flex items-center gap-2 py-1.5 px-2 rounded-md transition-colors",
                    enabled ? "hover:bg-muted/50" : "opacity-60 hover:opacity-100"
                  )}
                >
                  <Checkbox
                    id={`block-${activeSurface}-${block.id}`}
                    checked={enabled}
                    onCheckedChange={(v) => handleBlockToggle(block.id, !!v)}
                    className="h-4 w-4"
                  />
                  
                  <label
                    htmlFor={`block-${activeSurface}-${block.id}`}
                    className="flex-1 text-sm cursor-pointer"
                  >
                    {displayLabel}
                    {block.adminOnly && (
                      <Badge variant="outline" className="ml-2 text-[8px] px-1 py-0">
                        Admin
                      </Badge>
                    )}
                  </label>
                  
                  {/* Status Indicator */}
                  {enabled && (
                    <>
                      {status === "ok" && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Check className="h-3.5 w-3.5 text-green-500" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">All fields present</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      
                      {status === "missing" && blockWarnings.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-amber-500 hover:text-amber-600"
                            >
                              <AlertTriangle className="h-3.5 w-3.5" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3" align="end">
                            <div className="space-y-3">
                              <div className="space-y-1">
                                <p className="text-xs font-medium">Missing Fields</p>
                                <ul className="text-xs text-muted-foreground space-y-0.5">
                                  {blockWarnings.map((w, i) => (
                                    <li key={i} className="flex items-start gap-1">
                                      <span className="text-amber-500">•</span>
                                      <code className="text-[10px] bg-muted px-1 rounded">
                                        {w.missingField}
                                      </code>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs gap-1 flex-1"
                                  onClick={() => handleBlockToggle(block.id, false)}
                                >
                                  <EyeOff className="h-3 w-3" />
                                  Disable
                                </Button>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      className="h-7 text-xs gap-1 flex-1"
                                    >
                                      <Sparkles className="h-3 w-3" />
                                      Fallback
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs">Use fallback content in preview</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                      
                      {status === "fallback" && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3.5 w-3.5 text-blue-500" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Using fallback content</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };
  
  // CR309: Mode toggle (grouped vs reorder)
  const [viewMode, setViewMode] = useState<"grouped" | "reorder">("reorder");
  
  // CR309: Render a single draggable block item
  const renderDraggableBlock = (block: BlockMetadata, index: number) => {
    const enabled = getBlockToggleState(block.id, activeSurface, sections, v2BlockToggles, surfaceBlockToggles, styleOverrides);
    const status = getBlockStatus(block.id, enabled);
    const isFixed = fixedBlockIds.has(block.id);
    const displayLabel = getBlockDisplayLabel(block.id, dealType);
    
    return (
      <SortableBlockRow
        key={block.id}
        id={block.id}
        displayLabel={displayLabel}
        enabled={enabled}
        isFixed={isFixed}
        status={status}
        adminOnly={block.adminOnly}
        onToggle={handleBlockToggle}
      />
    );
  };
  
  // CR309: Get blocks in current order
  const orderedBlocks = useMemo(() => {
    const blockMap = new Map(surfaceBlocks.map(b => [b.id, b]));
    return currentBlockOrder
      .map(id => blockMap.get(id))
      .filter((b): b is BlockMetadata => b !== undefined);
  }, [surfaceBlocks, currentBlockOrder]);
  
  // CR312: Separate fixed and orderable blocks for proper DnD handling
  const { fixedTopBlocks, orderableBlocks, fixedBottomBlocks } = useMemo(() => {
    const fixedTop: BlockMetadata[] = [];
    const orderable: BlockMetadata[] = [];
    const fixedBottom: BlockMetadata[] = [];
    
    for (const block of orderedBlocks) {
      if (!fixedBlockIds.has(block.id)) {
        orderable.push(block);
      } else if (activeSurface === "details") {
        // CR451: Only back_header/back_nav are fixed at top
        if (block.id === "back_header" || block.id === "back_nav") {
          fixedTop.push(block);
        }
      } else {
        // On primary surface, fixed blocks go to top (none currently)
        fixedTop.push(block);
      }
    }
    
    return { fixedTopBlocks: fixedTop, orderableBlocks: orderable, fixedBottomBlocks: fixedBottom };
  }, [orderedBlocks, fixedBlockIds, activeSurface]);
  
  // CR312: Orderable block IDs for SortableContext (must be stable array of strings)
  const orderableBlockIds = useMemo(() => {
    return orderableBlocks.map(b => b.id);
  }, [orderableBlocks]);
  
  return (
    <div className={cn("space-y-3", className)}>
      {/* CR316: Surface Selector */}
      <div className="flex items-center gap-2 p-1 bg-muted/30 rounded-lg">
        <Button
          variant={activeSurface === "primary" ? "secondary" : "ghost"}
          size="sm"
          className={cn(
            "flex-1 h-8 text-xs gap-1.5",
            activeSurface === "primary" && "bg-background shadow-sm"
          )}
          onClick={() => setActiveSurface("primary")}
        >
          <Layers className="h-3.5 w-3.5" />
          Front (Primary)
        </Button>
        <Button
          variant={activeSurface === "details" ? "secondary" : "ghost"}
          size="sm"
          className={cn(
            "flex-1 h-8 text-xs gap-1.5",
            activeSurface === "details" && "bg-background shadow-sm"
          )}
          onClick={() => setActiveSurface("details")}
        >
          <LayoutList className="h-3.5 w-3.5" />
          Back (Details)
        </Button>
      </div>
      
      {/* Summary Row + View Mode Toggle */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">
            {enabledCount} blocks enabled
          </span>
          {hasCustomOrder && (
            <Badge variant="secondary" className="text-[8px] px-1">
              Custom order
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasCustomOrder && onSurfaceBlockOrderChange && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetOrder}
              className="h-6 text-[10px] gap-1 px-2"
            >
              <RotateCcw className="h-3 w-3" />
              Reset
            </Button>
          )}
          {warningCount > 0 && (
            <Badge variant="outline" className="text-[10px] gap-1 text-amber-600 border-amber-200 bg-amber-50">
              <AlertTriangle className="h-3 w-3" />
              {warningCount}
            </Badge>
          )}
        </div>
      </div>
      
      {/* CR312: Drag-and-drop reorderable block list with fixed blocks separated */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-1">
          {/* Fixed top blocks (non-sortable) */}
          {fixedTopBlocks.map((block, index) => renderDraggableBlock(block, index))}
          
          {/* Orderable blocks (sortable) */}
          <SortableContext 
            items={orderableBlockIds} 
            strategy={verticalListSortingStrategy}
          >
            {orderableBlocks.map((block, index) => renderDraggableBlock(block, index))}
          </SortableContext>
          
          {/* Fixed bottom blocks (non-sortable) */}
          {fixedBottomBlocks.map((block, index) => renderDraggableBlock(block, index))}
        </div>
        
        {/* Drag overlay */}
        <DragOverlay>
          {activeBlock ? (
            <div className="flex items-center gap-2 py-1.5 px-2 rounded-md border bg-background shadow-lg">
              <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm">{getBlockDisplayLabel(activeBlock.id, dealType)}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      
      {/* CR319: Accordions below block toggles */}
      <Separator className="my-4" />
      
      {/* CR319: Promo Badges Accordion */}
      {onPromoBadgesChange && (
        <PromoBadgesAccordion
          config={promoBadgesConfig}
          onChange={onPromoBadgesChange}
        />
      )}
      
      {/* CR319: Details Content Accordion */}
      {onDetailsContentChange && (
        <DetailsContentAccordion
          config={detailsContentConfig}
          onChange={onDetailsContentChange}
          className="mt-2"
        />
      )}
    </div>
  );
}

// ================================================================
// CR309: Sortable Block Row Component
// ================================================================

interface SortableBlockRowProps {
  id: string;
  displayLabel: string;
  enabled: boolean;
  isFixed: boolean;
  status: SectionStatus;
  adminOnly?: boolean;
  onToggle: (id: string, enabled: boolean) => void;
}

function SortableBlockRow({
  id,
  displayLabel,
  enabled,
  isFixed,
  status,
  adminOnly,
  onToggle,
}: SortableBlockRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    disabled: isFixed,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-2 py-1.5 px-2 rounded-md border bg-background transition-colors",
        isDragging && "opacity-50 border-primary/50 shadow-md",
        isFixed && "bg-muted/30",
        !enabled && "opacity-60"
      )}
    >
      {/* Drag Handle or Lock Icon */}
      {isFixed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Lock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Fixed position</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
        </button>
      )}
      
      {/* Checkbox */}
      <Checkbox
        id={`block-order-${id}`}
        checked={enabled}
        onCheckedChange={(v) => onToggle(id, !!v)}
        className="h-4 w-4 flex-shrink-0"
      />
      
      {/* Label */}
      <label
        htmlFor={`block-order-${id}`}
        className="flex-1 text-sm cursor-pointer truncate"
      >
        {displayLabel}
      </label>
      
      {/* Status Indicators */}
      {enabled && status === "ok" && (
        <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
      )}
      {enabled && status === "missing" && (
        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
      )}
      {adminOnly && (
        <Badge variant="outline" className="text-[8px] px-1 py-0 flex-shrink-0">
          Admin
        </Badge>
      )}
      {isFixed && (
        <Badge variant="outline" className="text-[8px] px-1 py-0 flex-shrink-0">
          Fixed
        </Badge>
      )}
    </div>
  );
}
